import React, { useEffect, useState } from 'react';
import { X, Search, Download, Landmark, Link2, Loader2, CheckCircle2 } from 'lucide-react';
import { apiService } from '../../services/api';
import { TaseCompany, TaseFiling } from '../../types';

interface TaseImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: string;
  /** Called after a successful import so the caller can refresh the file tree
      and start tracking the new document's processing progress. */
  onImported: (documentId: number, filename: string) => void;
}

/**
 * "ייבוא מהבורסה" — search the curated Maya filings catalog by company name
 * and import a report server-side (no manual download/upload). Also accepts a
 * pasted mayafiles.tase.co.il PDF link for filings not yet in the catalog.
 */
const TaseImportModal: React.FC<TaseImportModalProps> = ({ isOpen, onClose, projectId, onImported }) => {
  const [query, setQuery] = useState('');
  const [companies, setCompanies] = useState<TaseCompany[]>([]);
  const [loading, setLoading] = useState(false);
  const [importingUrl, setImportingUrl] = useState<string | null>(null);
  const [importedUrls, setImportedUrls] = useState<Set<string>>(new Set());
  const [pastedUrl, setPastedUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setLoading(true);
    apiService
      .searchTaseCatalog(query.trim() || undefined)
      .then(setCompanies)
      .catch(() => setError('טעינת קטלוג הדוחות נכשלה. נסה שוב.'))
      .finally(() => setLoading(false));
  }, [isOpen, query]);

  if (!isOpen) return null;

  const doImport = async (filing: { url: string; title?: string }, company?: string) => {
    setImportingUrl(filing.url);
    setError(null);
    try {
      const result = await apiService.importTaseFiling({
        url: filing.url,
        company,
        title: filing.title,
        project_id: projectId,
      });
      setImportedUrls((prev) => new Set(prev).add(filing.url));
      onImported(result.document_id, result.filename);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || 'הייבוא נכשל. ודא שהקישור תקין ונסה שוב.');
    } finally {
      setImportingUrl(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" dir="rtl">
      <div className="w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-4">
          <Landmark className="h-5 w-5 text-emerald-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-gray-800">ייבוא דוח מאתר הבורסה (מאיה)</h2>
            <p className="text-xs text-gray-500">חפש חברה ← בחר דוח ← הדוח יורד ונכנס לעיבוד אוטומטית</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="סגור">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 pt-4">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="שם חברה (למשל: ישראכרט, אדגר, תורפז)…"
              className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pr-9 pl-3 text-sm focus:border-emerald-400 focus:bg-white focus:outline-none"
            />
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> טוען קטלוג…
            </div>
          )}
          {!loading && companies.length === 0 && (
            <div className="text-sm text-gray-500 py-6 text-center">
              לא נמצאה חברה בקטלוג. אפשר להדביק קישור ישיר לדוח מ-maya.tase.co.il למטה.
            </div>
          )}
          {!loading &&
            companies.map((company) => (
              <div key={company.name} className="rounded-xl border border-gray-100">
                <div className="flex items-baseline gap-2 px-4 pt-3 pb-1">
                  <span className="font-semibold text-sm text-gray-800">{company.name}</span>
                  {company.sector && <span className="text-xs text-gray-400">{company.sector}</span>}
                </div>
                <ul>
                  {company.filings.map((filing: TaseFiling) => {
                    const done = importedUrls.has(filing.url);
                    const busy = importingUrl === filing.url;
                    return (
                      <li
                        key={filing.url}
                        className="flex items-center gap-3 px-4 py-2.5 border-t border-gray-50"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-gray-700 truncate">{filing.title}</div>
                          <div className="text-xs text-gray-400">
                            {filing.period} · {filing.year}
                            {filing.pages ? ` · ${filing.pages} עמ׳` : ''}
                          </div>
                        </div>
                        {done ? (
                          <span className="flex items-center gap-1 text-xs text-emerald-600">
                            <CheckCircle2 className="h-4 w-4" /> יובא — בעיבוד
                          </span>
                        ) : (
                          <button
                            onClick={() => doImport(filing, company.name)}
                            disabled={!!importingUrl}
                            className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-40"
                          >
                            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                            {busy ? 'מוריד…' : 'ייבא'}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
        </div>

        {/* Paste-URL fallback + errors */}
        <div className="border-t border-gray-100 px-5 py-4 space-y-2">
          {error && <div className="text-xs text-red-600">{error}</div>}
          <div className="flex items-center gap-2">
            <Link2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <input
              value={pastedUrl}
              onChange={(e) => setPastedUrl(e.target.value)}
              placeholder="או הדבק קישור לדוח ממאיה (עמוד הדוח או PDF)…"
              dir="ltr"
              className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-xs focus:border-emerald-400 focus:outline-none"
            />
            <button
              onClick={() => pastedUrl.trim() && doImport({ url: pastedUrl.trim() })}
              disabled={!pastedUrl.trim() || !!importingUrl}
              className="rounded-lg bg-gray-800 px-3 py-2 text-xs font-medium text-white hover:bg-gray-700 disabled:opacity-40"
            >
              ייבא מקישור
            </button>
          </div>
          <p className="text-[11px] text-gray-400">
            הקטלוג מתעדכן לפי רשימת החברות במעקב; דוח שחסר — העתיקו משורת הכתובת במאיה את
            קישור עמוד הדוח (maya.tase.co.il/he/reports/…) והדביקו כאן.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TaseImportModal;
